(() => {
  if (customElements.get("ingredient-spotlight")) return;

  class IngredientSpotlight extends HTMLElement {
    constructor() {
      super();
      this.cleanup = [];
      this.activeId = null;
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
    }

    connectedCallback() {
      this.sectionId =
        this.closest(".shopify-section")?.dataset?.sectionId || null;
      this.hotspots = Array.from(
        this.querySelectorAll("[data-ingredient-hotspot]")
      );
      this.items = Array.from(this.querySelectorAll("[data-ingredient-item]"));
      this.labels = Array.from(
        this.querySelectorAll("[data-ingredient-label]")
      );
      this.canHover =
        window.matchMedia &&
        window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      this.bindEvents();
      this.setDefaultActive();
      this.bindEditorEvents();
    }

    disconnectedCallback() {
      this.cleanup.forEach((remove) => remove());
      this.cleanup = [];
    }

    addListener(el, event, handler, options) {
      if (!el) return;
      el.addEventListener(event, handler, options);
      this.cleanup.push(() => el.removeEventListener(event, handler, options));
    }

    bindEditorEvents() {
      if (!window.Shopify || !Shopify.designMode) return;
      this.addListener(document, "shopify:block:select", this.onBlockSelect);
      this.addListener(document, "shopify:section:select", this.onSectionSelect);
      this.addListener(window, "shopify:block:select", this.onBlockSelect);
      this.addListener(window, "shopify:section:select", this.onSectionSelect);
    }

    onBlockSelect(event) {
      const blockId = event?.detail?.blockId;
      const sectionId = event?.detail?.sectionId;
      if (this.sectionId && sectionId && this.sectionId !== sectionId) return;
      if (!blockId) return;
      const hasBlock = this.items.some(
        (item) => item.dataset.ingredientItem === blockId
      );
      if (hasBlock) {
        this.setActive(blockId);
      }
    }

    onSectionSelect(event) {
      const sectionId = event?.detail?.sectionId;
      if (!sectionId) return;
      if (this.sectionId && this.sectionId === sectionId) {
        this.setDefaultActive();
      }
    }

    setDefaultActive() {
      const defaultItem = this.items.find((item) =>
        item.hasAttribute("data-default-active")
      );
      const id =
        (defaultItem && defaultItem.dataset.ingredientItem) ||
        (this.items[0] && this.items[0].dataset.ingredientItem);
      if (id) this.setActive(id);
    }

    setActive(id) {
      if (!id || id === this.activeId) return;
      this.activeId = id;

      this.hotspots.forEach((hotspot) => {
        const isActive = hotspot.dataset.ingredientHotspot === id;
        hotspot.classList.toggle("is-active", isActive);
        const ring = hotspot.querySelector("[data-ingredient-ring]");
        const dot = hotspot.querySelector("[data-ingredient-dot]");
        if (ring) {
          ring.classList.toggle("opacity-100", isActive);
          ring.classList.toggle("opacity-0", !isActive);
          ring.style.animation = isActive
            ? "ingredient-ripple 2s cubic-bezier(0, 0, 0.2, 1) infinite"
            : "";
        }
        if (dot) {
          dot.classList.toggle("bg-black", isActive);
          dot.classList.toggle("border-black", isActive);
          dot.classList.toggle("scale-110", isActive);
          dot.classList.toggle("bg-white", !isActive);
          dot.classList.toggle("border-black/40", !isActive);
          dot.classList.toggle("scale-100", !isActive);
        }
      });

      this.labels.forEach((label) => {
        if (label.offsetParent === null) return;
        const isActive = label.dataset.ingredientLabel === id;
        label.classList.toggle("is-active", isActive);
        label.classList.toggle("opacity-100", isActive);
        label.classList.toggle("translate-x-0", isActive);
        label.classList.toggle("opacity-0", !isActive);
        label.classList.toggle("-translate-x-2", !isActive);
      });

      this.items.forEach((item) => {
        const isActive = item.dataset.ingredientItem === id;
        item.classList.toggle("is-active", isActive);
        item.classList.toggle("border-foreground", isActive);
        item.classList.toggle("border-foreground/20", !isActive);
        const title = item.querySelector("[data-ingredient-title]");
        const detail = item.querySelector("[data-ingredient-detail]");
        if (title) {
          title.classList.toggle("opacity-100", isActive);
          title.classList.toggle("opacity-40", !isActive);
        }
        if (detail) {
          detail.classList.toggle("max-h-24", isActive);
          detail.classList.toggle("opacity-100", isActive);
          detail.classList.toggle("max-h-0", !isActive);
          detail.classList.toggle("opacity-0", !isActive);
        }
      });
    }

    bindEvents() {
      const activateFromEvent = (event) => {
        const id =
          event.currentTarget.dataset.ingredientHotspot ||
          event.currentTarget.dataset.ingredientItem;
        if (id) this.setActive(id);
      };

      const hoverEvent = this.canHover ? "mouseenter" : null;
      if (hoverEvent) {
        this.hotspots.forEach((hotspot) =>
          this.addListener(hotspot, hoverEvent, activateFromEvent)
        );
        this.items.forEach((item) =>
          this.addListener(item, hoverEvent, activateFromEvent)
        );
      }

      this.hotspots.forEach((hotspot) =>
        this.addListener(hotspot, "click", activateFromEvent)
      );
      this.items.forEach((item) =>
        this.addListener(item, "click", activateFromEvent)
      );
    }
  }

  customElements.define("ingredient-spotlight", IngredientSpotlight);
})();
